import { SettingOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import React, { useMemo } from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/shallow';

import type { ExtranetHotelExperience } from 'app/typings/core/extranet/hotel/experiences';

import { inventoryStore } from '../../../../InventoryStore';
import { useExperienceInventoryState } from '../../../../hooks';
import { useInventoryPermissions } from '../../../../hooks/useInventoryPermissions';
import { CollapseTable } from '../../../Table/CollapseTable/CollapseTable';
import styles from '../ExperiencesTab.module.scss';
import { useFilteredExperiences } from '../hooks/useFilteredExperiences';

import { ExperienceBookedRow } from './rows/ExperienceBookedRow';
import { ExperienceStatusRow } from './rows/ExperienceStatusRow';
import { ExperienceStockRow } from './rows/ExperienceStockRow';
import { formatExperienceName } from './utils';

type Props = {
  experience: ExtranetHotelExperience;
  categoryName?: string;
};

const ExperienceTableItemComponent: React.FC<Props> = ({
  experience,
  categoryName,
}) => {
  const { isEditMode, setExperienceStockConfig, setBulkEditDrawerOpen } =
    useStore(
      inventoryStore,
      useShallow((state) => ({
        isEditMode: state.isEditMode,
        setExperienceStockConfig: state.setExperienceStockConfig,
        setBulkEditDrawerOpen: state.setExperienceBulkEditDrawer,
      }))
    );
  const name = formatExperienceName(experience, categoryName);
  const { isInfiniteExperience } = useFilteredExperiences();
  const { canAccessBulkEdit } = useInventoryPermissions();
  const { inventory } = useExperienceInventoryState();

  const isCollapsed = useMemo(() => {
    if (isEditMode) {
      return false;
    }

    if (experience.isAlwaysIncludedWithNight) {
      return true;
    }

    if (!inventory) {
      return true;
    }

    const hasLimitedStock = Object.values(inventory).some((dateData) => {
      const expOpening = dateData.experiences[experience.id];

      return (
        expOpening?.remainingStock !== undefined &&
        expOpening.remainingStock !== null
      );
    });

    if (!hasLimitedStock) {
      return true;
    }

    const isAlwaysClosed = Object.values(inventory).every((dateData) => {
      const expOpening = dateData.experiences[experience.id];

      return expOpening?.isClosed === true;
    });

    if (isAlwaysClosed) {
      return true;
    }
  }, [inventory, experience, isEditMode]);

  const isUnlimitedStock = isInfiniteExperience(experience.id);

  if (!inventory) {
    return null;
  }

  return (
    <>
      <CollapseTable
        key={`exp-${experience.id}`}
        images={experience.emoji ? [experience.emoji] : undefined}
        title={name || 'Unnamed Experience'}
        keyPanel={`exp-${experience.id}`}
        isClosed={isCollapsed}
        extraHeader={
          <>
            {canAccessBulkEdit && !experience.isAlwaysIncludedWithNight && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();

                  setBulkEditDrawerOpen(experience.id, true);
                }}
                disabled={isEditMode}
              >
                Bulk edit
              </Button>
            )}
            <Button
              onClick={(e) => {
                e.stopPropagation();

                setExperienceStockConfig(experience.id, true);
              }}
              disabled={isEditMode}
              className={styles.configureStockButton}
            >
              <SettingOutlined />
            </Button>
          </>
        }
      >
        <ExperienceStatusRow experienceId={experience.id} />
        <ExperienceStockRow
          experienceId={experience.id}
          isUnlimitedStock={isUnlimitedStock}
          isAlwaysIncludedWithNight={experience.isAlwaysIncludedWithNight}
        />
        <ExperienceBookedRow experienceId={experience.id} />
      </CollapseTable>
    </>
  );
};

export const ExperienceTableItem = React.memo(ExperienceTableItemComponent);
